/**
 * LeuningLab: houdt de leuninghouders in de winkelwagen gelijk aan wat de leuningen erin
 * nodig hebben.
 *
 * Een leuning draagt de eigenschap "Houders: 2 × Essential" of "Houders: 2 × Premium". Dat
 * aantal geldt per leuning. Na elke wijziging van de winkelwagen telt dit script op hoeveel
 * houders er per variant horen te liggen en zet het de bijbehorende regels daarop. Zonder
 * leuning verdwijnen die houders dus ook.
 *
 * Essential-houders horen altijd bij een leuning. Premium-houders zijn ook los te koop:
 * alleen de regel met de eigenschap "_bij_leuning" volgt de leuning, een los bestelde regel
 * blijft onaangeroerd.
 */
(() => {
  const configElement = document.getElementById('ll-houder-config');
  if (!configElement) return;

  const config = JSON.parse(configElement.textContent);
  const root = window.Shopify?.routes?.root ?? '/';

  const SOURCE = 'll-houder-sync';
  const BOUND_PROPERTY = '_bij_leuning';
  const BRACKET_PATTERN = /^(\d+)\s*×\s*(Essential|Premium)$/;
  const COLOURS = ['zwart', 'wit', 'greige', 'brons'];
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let queue = Promise.resolve();

  /** Ronde leuningen krijgen een hol zadel, alle andere profielen een plat zadel. */
  const saddleFor = (handrail) => (handrail.handle.includes('rond') ? 'Hol' : 'Plat');

  const colourFor = (handrail) => {
    const colour = COLOURS.find((name) => handrail.handle.includes(`-${name}`));
    return colour ? colour.charAt(0).toUpperCase() + colour.slice(1) : null;
  };

  const bracketVariantFor = (handrail, type) => {
    const saddle = saddleFor(handrail);
    if (type === 'Essential') return config.essentialVariants[saddle];
    return config.premiumVariants[`${saddle}|${colourFor(handrail)}`];
  };

  /** Geeft per houdervariant het aantal dat bij de leuningen in de winkelwagen hoort. */
  const expectedQuantities = (cart) => {
    const expected = new Map();

    for (const item of cart.items) {
      const match = BRACKET_PATTERN.exec(item.properties?.Houders ?? '');
      if (!match) continue;

      const variantId = bracketVariantFor(item, match[2]);
      if (!variantId) continue;

      expected.set(variantId, (expected.get(variantId) ?? 0) + Number(match[1]) * item.quantity);
    }

    return expected;
  };

  const isBound = (item) => Boolean(item.properties?.[BOUND_PROPERTY]);

  const essentialChanges = (cart, expected) => {
    const updates = {};

    for (const variantId of Object.values(config.essentialVariants)) {
      const quantity = expected.get(variantId) ?? 0;
      const current = cart.items
        .filter((item) => item.variant_id === variantId)
        .reduce((total, item) => total + item.quantity, 0);

      if (current !== quantity) updates[variantId] = quantity;
    }

    return { updates, additions: [] };
  };

  const premiumChanges = (cart, expected) => {
    const updates = {};
    const additions = [];

    for (const variantId of Object.values(config.premiumVariants)) {
      const quantity = expected.get(variantId) ?? 0;
      const line = cart.items.find((item) => item.variant_id === variantId && isBound(item));

      if (line && line.quantity !== quantity) updates[line.key] = quantity;
      if (!line && quantity > 0) additions.push({ id: variantId, quantity, properties: { [BOUND_PROPERTY]: 'ja' } });
    }

    return { updates, additions };
  };

  const requiredChanges = (cart) => {
    const expected = expectedQuantities(cart);
    const essential = essentialChanges(cart, expected);
    const premium = premiumChanges(cart, expected);

    return {
      updates: { ...essential.updates, ...premium.updates },
      additions: premium.additions,
    };
  };

  const fetchJson = async (url, options) => {
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`Request to ${url} failed with status ${response.status}`);
    return response.json();
  };

  const postJson = (url, body) =>
    fetchJson(url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });

  /** Laat de winkelwagen, het uitschuifpaneel en het tellertje in de header zich verversen. */
  const announce = (cart) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data: { source: SOURCE, itemCount: cart.item_count },
        },
      })
    );
  };

  const reconcile = async () => {
    const cart = await fetchJson(`${root}cart.js`);
    const { updates, additions } = requiredChanges(cart);

    const hasUpdates = Object.keys(updates).length > 0;
    const hasAdditions = additions.length > 0;
    if (!hasUpdates && !hasAdditions) return;

    if (hasUpdates) await postJson(`${root}cart/update.js`, { updates });
    if (hasAdditions) await postJson(`${root}cart/add.js`, { items: additions });

    announce(await fetchJson(`${root}cart.js`));
  };

  /** Rondes lopen na elkaar, zodat twee wijzigingen vlak achter elkaar elkaar niet overschrijven. */
  const sync = () => {
    queue = queue.then(reconcile).catch((error) => console.error(error));
    return queue;
  };

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    sync();
  });

  if (config.cartItemCount > 0) sync();

  window.LLHouders = { sync };
})();
